import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { intakeAPI, publicAgreementsAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { 
  AlertCircle, 
  User, 
  Building2, 
  Users, 
  Check, 
  ChevronRight,
  ChevronLeft,
  Home,
  Plus,
  Trash2,
  Upload,
  Mail,
  FileText,
  Shield,
  CreditCard,
  Loader2,
  Search,
  X,
  Info,
  AlertTriangle,
  CheckCircle,
  Lock,
  ArrowUpRight
} from 'lucide-react';
import { toast } from '@/utils/portalNotifications';
import { v4 as uuidv4 } from 'uuid';
import { PropertyLimitPrompt } from '../components/UpgradePrompt';
import { usePortfolioUsageContext } from '../hooks/usePortfolioUsageContext';
import { BRAND_LOGO_URL, branding } from '../config/branding';
import { JURISDICTION_OPTIONS } from '../utils/jurisdictionComplianceCopy';
import {
  INTAKE_EMAIL_DEBOUNCE_MS,
  INTAKE_EMAIL_DUPLICATE_MESSAGE,
  canonicalIntakeEmail,
  isIntakeEmailFormatValid,
} from '../utils/intakeEmail';
import { getPropertyDisplayName } from '../utils/propertyDisplayName';
import { renderAgreementForDisplay, validateAgreementRender } from '../utils/agreementRender';
import { isFullUkPostcode, normalizeUkPostcode } from '../utils/ukPostcode';
import { UkPostcodeLookupField } from '../components/address/UkPostcodeLookupField';
import { useUkPostcodeLookup } from '../hooks/useUkPostcodeLookup';

// Plan limits - NEW PLAN STRUCTURE (must match backend plan_registry.py)
const PLAN_LIMITS = {
  // New plan codes
  PLAN_1_SOLO: 2,
  PLAN_2_PORTFOLIO: 10,
  PLAN_3_PRO: 25,
  // Legacy codes (for backward compatibility)
  PLAN_1: 2,
  PLAN_2_5: 10,
  PLAN_6_15: 25
};

// Plan names for display
const PLAN_NAMES = {
  PLAN_1_SOLO: 'Solo Landlord',
  PLAN_2_PORTFOLIO: 'Portfolio',
  PLAN_3_PRO: 'Professional',
  // Legacy
  PLAN_1: 'Solo Landlord',
  PLAN_2_5: 'Portfolio',
  PLAN_6_15: 'Professional'
};

/** Map legacy / API plan codes to canonical intake billing_plan values. */
function normalizeIntakeBillingPlanCode(code) {
  if (code == null || code === '') return null;
  const c = String(code).trim().toUpperCase();
  const legacy = {
    PLAN_1: 'PLAN_1_SOLO',
    PLAN_2_5: 'PLAN_2_PORTFOLIO',
    PLAN_6_15: 'PLAN_3_PRO',
  };
  return legacy[c] || c;
}

function isValidIntakeBillingPlanCode(code) {
  return Boolean(code && Object.prototype.hasOwnProperty.call(PLAN_LIMITS, code));
}

/** Align with backend invite_entry_channel: link when URL ?invite= matches the code in the field. */
function pilotInviteEntryChannelFromSources(pilotInviteCode, inviteFromQuery) {
  const p = (pilotInviteCode || '').trim().toUpperCase().replace(/\s+/g, '');
  const u = (inviteFromQuery || '').trim().toUpperCase().replace(/\s+/g, '');
  if (u && p === u) return 'link';
  return 'manual';
}

// Property types
const PROPERTY_TYPES = [
  { value: 'flat', label: 'Flat/Apartment' },
  { value: 'house', label: 'House' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'terraced', label: 'Terraced House' },
  { value: 'semi-detached', label: 'Semi-Detached' },
  { value: 'detached', label: 'Detached' },
  { value: 'commercial', label: 'Commercial' }
];

// Occupancy types
const OCCUPANCY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'multi_family', label: 'Multi-family occupancy' },
  { value: 'student', label: 'Student Let' },
  { value: 'professional', label: 'Professional Let' },
  { value: 'mixed', label: 'Mixed Use' }
];

// Licence types
const LICENCE_TYPES = [
  { value: 'selective', label: 'Selective Licensing' },
  { value: 'additional', label: 'Additional Licensing' },
  { value: 'mandatory_hmo', label: 'Mandatory HMO Licence' }
];

// Licence statuses
const LICENCE_STATUSES = [
  { value: 'applied', label: 'Applied' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'expired', label: 'Expired' },
  { value: 'unknown', label: 'Unknown' }
];

// Coerce for API: backend expects int or null for bedrooms (input value is always string from <input>)
function coerceBedrooms(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n < 0) return null;
  const intVal = Math.floor(n);
  return intVal;
}

// Coerce for API: backend expects boolean
function coerceBool(v) {
  return v === true || v === 'true' || v === 1;
}

/** Build submit payload with numeric and boolean fields coerced for /api/intake/submit */
export function buildIntakeSubmitPayload(formData, intakeSessionId, marketing = {}) {
  const canonEmail = canonicalIntakeEmail(formData.email) || (formData.email || '').trim();
  const payload = {
    ...formData,
    email: canonEmail || formData.email,
    intake_session_id: intakeSessionId || null,
    email_upload_consent: coerceBool(formData.email_upload_consent),
    consent_data_processing: coerceBool(formData.consent_data_processing),
    consent_service_boundary: coerceBool(formData.consent_service_boundary),
    properties: (formData.properties || []).map((p) => ({
      ...p,
      bedrooms: coerceBedrooms(p.bedrooms),
      is_hmo: coerceBool(p.is_hmo),
      has_gas_supply:
        p.has_gas_supply === null || p.has_gas_supply === undefined || p.has_gas_supply === ''
          ? null
          : coerceBool(p.has_gas_supply),
      tenancy_active: coerceBool(p.tenancy_active),
      deposit_taken: coerceBool(p.deposit_taken),
      furnished:
        p.furnished === null || p.furnished === undefined || p.furnished === ''
          ? null
          : coerceBool(p.furnished),
      has_communal_areas: coerceBool(p.has_communal_areas),
    })),
  };
  if (marketing.lead_id) payload.lead_id = marketing.lead_id;
  if (marketing.source) payload.source = marketing.source;
  return payload;
}

// Certificate availability options
const CERT_OPTIONS = [
  { value: 'YES', label: 'Yes, I have it' },
  { value: 'NO', label: "No, I don't have it" },
  { value: 'UNSURE', label: 'Unsure' }
];

const IntakePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState(null);
  const [plans, setPlans] = useState([]);
  const [intakeSessionId] = useState(() => uuidv4());
  const [marketing, setMarketing] = useState({ lead_id: null, source: null });

  /** After a successful submit, allow checkout retry without re-posting submit (e.g. payment or acceptance errors). Cleared when leaving step 5. */
  const [resumeClientId, setResumeClientId] = useState(null);
  /** Step 1 live email: idle | checking | available | taken | error (network / server). Authoritative rule remains on submit. */
  const [emailAvailability, setEmailAvailability] = useState('idle');
  const emailDebounceRef = useRef(null);
  const emailLatestRef = useRef('');
  /** Shown with duplicate-email message so users can jump to portal login. */
  const [showLoginLink, setShowLoginLink] = useState(false);
  const [agreementCurrent, setAgreementCurrent] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementFetchError, setAgreementFetchError] = useState('');
  const [agreementFetchDiagnostic, setAgreementFetchDiagnostic] = useState(null);
  const [agreementRetryNonce, setAgreementRetryNonce] = useState(0);
  const [serviceAgreementAccepted, setServiceAgreementAccepted] = useState(false);
  const [pilotInviteCode, setPilotInviteCode] = useState('');
  const [pilotInviteValidation, setPilotInviteValidation] = useState(null);
  const [pilotInviteValidating, setPilotInviteValidating] = useState(false);

  // Form data state (initial plan from URL ?plan=)
  const planParam = searchParams.get('plan');
  const inviteParam = searchParams.get('invite') || searchParams.get('invite_code');
  const leadIdParam = searchParams.get('lead_id');
  const leadTokenParam = searchParams.get('lead_token');
  const sourceParam = searchParams.get('from');
  const initialPlan = (planParam && ['PLAN_1_SOLO', 'PLAN_2_PORTFOLIO', 'PLAN_3_PRO', 'PLAN_1', 'PLAN_2_5', 'PLAN_6_15'].includes(planParam)) ? planParam : 'PLAN_1_SOLO';
  const [formData, setFormData] = useState({
    // Step 1: Your Details
    full_name: '',
    email: '',
    client_type: '',
    company_name: '',
    preferred_contact: 'EMAIL',
    phone: '',
    
    // Step 2: Plan - NEW DEFAULT (or from ?plan=)
    billing_plan: initialPlan,
    
    // Step 3: Properties
    properties: [{
      nickname: '',
      postcode: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      property_type: 'house',
      jurisdiction: '',
      is_hmo: false,
      has_gas_supply: null,
      tenancy_active: false,
      deposit_taken: false,
      furnished: null,
      has_communal_areas: false,
      bedrooms: '',
      occupancy: 'single_family',
      council_name: '',
      council_code: '',
      licence_required: '',
      licence_type: '',
      licence_status: '',
      managed_by: 'LANDLORD',
      send_reminders_to: 'LANDLORD',
      agent_name: '',
      agent_email: '',
      agent_phone: '',
      cert_gas_safety: '',
      cert_eicr: '',
      cert_epc: '',
      cert_licence: ''
    }],
    
    // Step 4: Preferences & Consents
    document_submission_method: '',
    email_upload_consent: false,
    consent_data_processing: false,
    consent_service_boundary: false
  });

  // Property limit state for upgrade prompts
  const [propertyLimitError, setPropertyLimitError] = useState(null);
  const [prefillFromRiskCheck, setPrefillFromRiskCheck] = useState(false);
  const [leadPropertyCountHint, setLeadPropertyCountHint] = useState(null);
  /** UX-only: plan suggested from verified lead_token + server recommendation (not billing authority). */
  const [intakePlanRecommendation, setIntakePlanRecommendation] = useState(null);

  // URL marketing params (lead_id, from=risk-check) for conversion linking
  useEffect(() => {
    if (leadIdParam && leadIdParam.trim()) {
      setMarketing({ lead_id: leadIdParam.trim(), source: (sourceParam && sourceParam.trim()) || 'risk-check' });
    }
  }, [leadIdParam, sourceParam]);

  // lead_token prefill: fetch lead-from-token and prefill form (server may suggest plan from risk-check data)
  useEffect(() => {
    if (!leadTokenParam || !leadTokenParam.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await intakeAPI.getLeadFromToken(leadTokenParam.trim());
        if (cancelled) return;
        setMarketing({ lead_id: data.lead_id || leadIdParam, source: (sourceParam && sourceParam.trim()) || 'risk-check' });
        const normalizedRec = normalizeIntakeBillingPlanCode(data.recommended_plan_code);
        const recApplied =
          data.recommendation_basis === 'property_count' &&
          normalizedRec &&
          isValidIntakeBillingPlanCode(normalizedRec);
        setFormData((prev) => {
          const next = { ...prev };
          if (data.full_name != null && data.full_name !== '') next.full_name = String(data.full_name).trim();
          else if (data.first_name != null && data.first_name !== '') next.full_name = String(data.first_name).trim();
          if (data.email != null && data.email !== '') next.email = String(data.email).trim();
          if (data.phone != null && data.phone !== '') next.phone = String(data.phone).trim();
          if (Array.isArray(prev.properties) && prev.properties.length > 0 && data.any_hmo === true) {
            next.properties = prev.properties.map((p, i) => (i === 0 ? { ...p, is_hmo: true } : p));
          }
          if (data.tracking_method != null && data.tracking_method !== '' && !prev.document_submission_method) {
            const t = String(data.tracking_method).toLowerCase();
            if (t.includes('automated')) next.document_submission_method = 'UPLOAD';
            else if (t.includes('manual') || t.includes('spreadsheet')) next.document_submission_method = 'EMAIL';
          }
          if (recApplied) {
            next.billing_plan = normalizedRec;
          }
          return next;
        });
        if (recApplied) {
          const pc =
            data.recommendation_property_count != null && data.recommendation_property_count !== ''
              ? Number(data.recommendation_property_count)
              : null;
          setIntakePlanRecommendation({
            basis: 'property_count',
            planCode: normalizedRec,
            propertyCount: Number.isFinite(pc) ? pc : null,
          });
        } else {
          setIntakePlanRecommendation(null);
        }
        setPrefillFromRiskCheck(true);
        const count = data.property_count != null ? Math.max(1, parseInt(data.property_count, 10) || 1) : null;
        setLeadPropertyCountHint(count > 1 ? count : null);
      } catch (err) {
        if (!cancelled)
          toast.error(err.response?.data?.detail || 'This link is no longer valid—start again from the risk check.');
      }
    })();
    return () => { cancelled = true; };
  }, [leadTokenParam, sourceParam, leadIdParam]);

  useEffect(() => {
    emailLatestRef.current = formData.email;
  }, [formData.email]);

  useEffect(
    () => () => {
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    },
    [],
  );

  const runEmailAvailabilityCheck = useCallback(async () => {
    const raw = (emailLatestRef.current || '').trim();
    if (!isIntakeEmailFormatValid(raw)) {
      setEmailAvailability('idle');
      return;
    }
    const requestedCanon = canonicalIntakeEmail(raw);
    setEmailAvailability('checking');
    try {
      const res = await intakeAPI.checkEmailAvailability(raw);
      if (canonicalIntakeEmail(emailLatestRef.current) !== requestedCanon) {
        return;
      }
      if (res.data?.available) {
        setEmailAvailability('available');
      } else {
        setEmailAvailability('taken');
      }
    } catch (e) {
      if (canonicalIntakeEmail(emailLatestRef.current) !== requestedCanon) {
        return;
      }
      setEmailAvailability('error');
    }
  }, []);

  const handleStep1EmailChange = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, email: value }));
      emailLatestRef.current = value;
      setShowLoginLink(false);
      setError('');
      setEmailAvailability('idle');
      if (emailDebounceRef.current) {
        clearTimeout(emailDebounceRef.current);
        emailDebounceRef.current = null;
      }
      if (!isIntakeEmailFormatValid(value)) {
        return;
      }
      emailDebounceRef.current = setTimeout(() => {
        emailDebounceRef.current = null;
        void runEmailAvailabilityCheck();
      }, INTAKE_EMAIL_DEBOUNCE_MS);
    },
    [runEmailAvailabilityCheck],
  );

  const handleStep1EmailBlur = useCallback(() => {
    if (emailDebounceRef.current) {
      clearTimeout(emailDebounceRef.current);
      emailDebounceRef.current = null;
    }
    void runEmailAvailabilityCheck();
  }, [runEmailAvailabilityCheck]);

  // Risk-check prefill: run one availability check when prefill completes (same render as updated email).
  // Read email from emailLatestRef so deps stay stable (ref is synced whenever formData.email changes).
  useEffect(() => {
    if (!prefillFromRiskCheck || step !== 1) return;
    const raw = (emailLatestRef.current || '').trim();
    if (!isIntakeEmailFormatValid(raw)) return;
    const tid = setTimeout(() => void runEmailAvailabilityCheck(), 0);
    return () => clearTimeout(tid);
  }, [prefillFromRiskCheck, step, runEmailAvailabilityCheck]);

  const validatePilotInviteForPlan = useCallback(
    async (code, planCode, email) => {
      const trimmed = (code || '').trim();
      if (!trimmed) {
        setPilotInviteValidation(null);
        return;
      }
      setPilotInviteValidating(true);
      try {
        const res = await intakeAPI.validatePilotInvite({
          code: trimmed,
          plan_code: planCode || formData.billing_plan,
          email: email || formData.email || undefined,
          entry_channel: pilotInviteEntryChannelFromSources(code || pilotInviteCode, inviteParam),
        });
        setPilotInviteValidation(res.data || { valid: false, message: 'Invalid response' });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const message =
          typeof detail === 'string'
            ? detail
            : detail?.message || err?.response?.data?.message || 'This invite code could not be applied.';
        setPilotInviteValidation({ valid: false, message });
      } finally {
        setPilotInviteValidating(false);
      }
    },
    [formData.billing_plan, formData.email, inviteParam, pilotInviteCode],
  );

  // Pre-fill invite from distribution URL (?invite=CODE)
  useEffect(() => {
    const raw = (inviteParam || '').trim();
    if (!raw) return;
    setPilotInviteCode(raw.toUpperCase().replace(/\s+/g, ''));
    void validatePilotInviteForPlan(raw, initialPlan, '');
  }, [inviteParam, initialPlan, validatePilotInviteForPlan]);

  // Load plans on mount (and when invite/plan context changes)
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const params = {};
        if ((pilotInviteCode || inviteParam || '').trim()) {
          params.invite = (pilotInviteCode || inviteParam || '').trim();
          params.invite_entry_channel = pilotInviteEntryChannelFromSources(pilotInviteCode, inviteParam);
        }
        if (formData.billing_plan) params.plan = formData.billing_plan;
        if (formData.email) params.email = formData.email;
        const response = await intakeAPI.getPlans(params);
        setPlans(response.data.plans);
      } catch (err) {
        console.error('Failed to load plans:', err);
      }
    };
    loadPlans();
  }, [pilotInviteCode, inviteParam, formData.billing_plan, formData.email]);

  useEffect(() => {
    if (step !== 5) {
      setResumeClientId(null);
      setServiceAgreementAccepted(false);
    }
  }, [step]);

  const renderedAgreement = useMemo(
    () => renderAgreementForDisplay(agreementCurrent),
    [agreementCurrent],
  );
  const agreementRenderValidation = useMemo(
    () => validateAgreementRender(renderedAgreement),
    [renderedAgreement],
  );

  const agreementPreviewRequestBody = useMemo(() => {
    const intakePayload = buildIntakeSubmitPayload(formData, intakeSessionId, marketing);
    const base = resumeClientId
      ? { intake_session_id: intakeSessionId, client_id: resumeClientId }
      : { intake_session_id: intakeSessionId, intake: intakePayload };
    const trimmed = (pilotInviteCode || '').trim();
    if (trimmed) {
      base.invite_code = trimmed.toUpperCase().replace(/\s+/g, '');
      base.invite_entry_channel = pilotInviteEntryChannelFromSources(pilotInviteCode, inviteParam);
    }
    return base;
  }, [formData, intakeSessionId, marketing, resumeClientId, pilotInviteCode, inviteParam]);

  useEffect(() => {
    if (step !== 5) return;
    let cancelled = false;
    let timer = null;
    setAgreementLoading(true);
    setAgreementFetchError('');
    setAgreementFetchDiagnostic(null);

    const load = async () => {
      try {
        const res = await intakeAPI.previewAgreement(agreementPreviewRequestBody);
        if (!cancelled) {
          setAgreementCurrent(res.data || null);
          setServiceAgreementAccepted(false);
          setAgreementFetchError('');
          setAgreementFetchDiagnostic(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAgreementCurrent(null);
          const d = err.response?.data?.detail;
          const requestId =
            typeof d === 'object' && d && d.request_id
              ? d.request_id
              : err.response?.data?.request_id || null;
          const errorCode =
            typeof d === 'object' && d && d.error_code
              ? d.error_code
              : err.response?.data?.error_code || null;
          const issues =
            typeof d === 'object' && d && Array.isArray(d.validation_issues) && d.validation_issues.length
              ? ` ${d.validation_issues.join('; ')}`
              : '';
          const hasPostcodeIssue =
            typeof d === 'object' &&
            d &&
            Array.isArray(d.validation_issues) &&
            d.validation_issues.some((x) => String(x).includes('client_postcode_malformed_or_truncated'));
          const baseMsg =
            typeof d === 'object' && d && d.message
              ? `${d.message}${issues}`
              : typeof d === 'string'
                ? d
                : 'Could not load the service agreement preview. Please refresh and try again.';
          const msg = hasPostcodeIssue
            ? `Please check the full postcode. It appears incomplete. Go back to property details and enter a complete UK postcode (e.g. KY10 1AA).${issues}`
            : baseMsg;
          setAgreementFetchError(msg);
          setAgreementFetchDiagnostic(requestId || errorCode ? { request_id: requestId, error_code: errorCode } : null);
          setServiceAgreementAccepted(false);
        }
      } finally {
        if (!cancelled) setAgreementLoading(false);
      }
    };

    timer = setTimeout(load, 400);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [step, agreementPreviewRequestBody, agreementRetryNonce]);

  // Step validation
  const validateStep = (stepNum) => {
    setError('');
    setShowLoginLink(false);
    
    switch (stepNum) {
      case 1:
        if (!formData.full_name.trim()) {
          setError('Full name is required');
          return false;
        }
        if (!isIntakeEmailFormatValid(formData.email)) {
          setError('Valid email is required');
          return false;
        }
        if (emailAvailability === 'checking') {
          setError('Please wait while we verify your email address.');
          return false;
        }
        if (emailAvailability === 'taken') {
          setError(INTAKE_EMAIL_DUPLICATE_MESSAGE);
          setShowLoginLink(true);
          return false;
        }
        if (emailAvailability === 'error') {
          setError(
            'Could not verify email right now. Check your connection and use “Retry check”, then continue.',
          );
          return false;
        }
        if (emailAvailability !== 'available') {
          setError(
            'Please wait until we confirm your email is available, or leave the field to finish verification.',
          );
          return false;
        }
        if (!formData.client_type) {
          setError('Please select your client type');
          return false;
        }
        if ((formData.client_type === 'COMPANY' || formData.client_type === 'AGENT') && !formData.company_name.trim()) {
          setError('Company name is required');
          return false;
        }
        if ((formData.preferred_contact === 'SMS' || formData.preferred_contact === 'BOTH') && !formData.phone.trim()) {
          setError('Phone number is required when SMS is enabled');
          return false;
        }
        return true;
        
      case 2:
        if (!formData.billing_plan) {
          setError('Please select a plan');
          return false;
        }
        return true;
        
      case 3:
        const maxProps = PLAN_LIMITS[formData.billing_plan] || 1;
        if (formData.properties.length === 0) {
          setError('At least one property is required');
          return false;
        }
        if (formData.properties.length > maxProps) {
          setError(`Maximum ${maxProps} properties allowed for your plan`);
          return false;
        }
        for (let i = 0; i < formData.properties.length; i++) {
          const prop = formData.properties[i];
          if (!prop.postcode.trim()) {
            setError(`Property ${i + 1}: Postcode is required`);
            return false;
          }
          if (!isFullUkPostcode(prop.postcode)) {
            setError(
              `Property ${i + 1}: Please check the full postcode. It appears incomplete.`,
            );
            return false;
          }
          if (!prop.address_line_1.trim()) {
            setError(`Property ${i + 1}: Address line 1 is required`);
            return false;
          }
          if (!prop.city.trim()) {
            setError(`Property ${i + 1}: City is required`);
            return false;
          }
          if (!String(prop.jurisdiction || '').trim()) {
            setError(`Property ${i + 1}: Jurisdiction is required`);
            return false;
          }
          if ((prop.send_reminders_to === 'AGENT' || prop.send_reminders_to === 'BOTH')) {
            if (!prop.agent_name.trim() || !prop.agent_email.trim()) {
              setError(`Property ${i + 1}: Agent name and email are required`);
              return false;
            }
          }
        }
        return true;
        
      case 4:
        if (!formData.document_submission_method) {
          setError('Please select a document submission method');
          return false;
        }
        if (formData.document_submission_method === 'EMAIL' && !formData.email_upload_consent) {
          setError('Please accept the email upload consent');
          return false;
        }
        // When UPLOAD: files are optional; when EMAIL: no uploads required
        if (!formData.consent_data_processing) {
          setError('GDPR consent is required');
          return false;
        }
        if (!formData.consent_service_boundary) {
          setError('Service boundary acknowledgment is required');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const nextStep = async () => {
    if (!validateStep(step)) return;
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setError('');
    setErrorDetail(null);
    setShowLoginLink(false);
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const goToStep = (stepNum) => {
    // Only allow going back or staying on current step
    if (stepNum <= step) {
      setError('');
      setErrorDetail(null);
      setShowLoginLink(false);
      setStep(stepNum);
      window.scrollTo(0, 0);
    }
  };

  // Property management – single add path; cap enforced here (no add when currentCount >= maxProps)
  const addProperty = async () => {
    const maxProps = PLAN_LIMITS[formData.billing_plan] ?? 2;
    const currentCount = formData.properties.length;

    // Block immediately when at or over cap – no UI path can add beyond cap
    if (currentCount >= maxProps) {
      let upgradePlan = null;
      let upgradePlanName = null;
      let upgradeLimit = null;
      if (formData.billing_plan === 'PLAN_1_SOLO' || formData.billing_plan === 'PLAN_1') {
        upgradePlan = 'PLAN_2_PORTFOLIO';
        upgradePlanName = 'Portfolio';
        upgradeLimit = 10;
      } else if (formData.billing_plan === 'PLAN_2_PORTFOLIO' || formData.billing_plan === 'PLAN_2_5') {
        upgradePlan = 'PLAN_3_PRO';
        upgradePlanName = 'Professional';
        upgradeLimit = 25;
      }
      setPropertyLimitError({
        currentLimit: maxProps,
        requestedCount: currentCount + 1,
        currentPlan: PLAN_NAMES[formData.billing_plan] || formData.billing_plan,
        upgradePlan,
        upgradePlanName,
        upgradeLimit
      });
      toast.error(`You've reached the maximum of ${maxProps} properties for the ${PLAN_NAMES[formData.billing_plan] || 'current'} plan.`);
      return;
    }

    setPropertyLimitError(null);
    const newCount = currentCount + 1;

    // Optionally validate with backend (belt and suspenders)
    try {
      const response = await intakeAPI.validatePropertyCount(formData.billing_plan, newCount);
      if (!response.data.allowed) {
        setPropertyLimitError({
          currentLimit: response.data.current_limit,
          requestedCount: newCount,
          currentPlan: PLAN_NAMES[formData.billing_plan] || formData.billing_plan,
          upgradePlan: response.data.upgrade_to,
          upgradePlanName: response.data.upgrade_to_name,
          upgradeLimit: response.data.upgrade_to_limit
        });
        toast.error(response.data.error || 'Property limit exceeded');
        return;
      }
    } catch (err) {
      // If validation fails, still allow (UI already checked)
      console.warn('Backend validation failed, using frontend check:', err);
    }
    
    setFormData({
      ...formData,
      properties: [...formData.properties, {
        nickname: '',
        postcode: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        property_type: 'house',
        jurisdiction: '',
        is_hmo: false,
        has_gas_supply: null,
        tenancy_active: false,
        deposit_taken: false,
        furnished: null,
        has_communal_areas: false,
        bedrooms: '',
        occupancy: 'single_family',
        council_name: '',
        council_code: '',
        licence_required: '',
        licence_type: '',
        licence_status: '',
        managed_by: 'LANDLORD',
        send_reminders_to: 'LANDLORD',
        agent_name: '',
        agent_email: '',
        agent_phone: '',
        cert_gas_safety: '',
        cert_eicr: '',
        cert_epc: '',
        cert_licence: ''
      }]
    });
  };

  const removeProperty = (index) => {
    if (formData.properties.length > 1) {
      setFormData({
        ...formData,
        properties: formData.properties.filter((_, i) => i !== index)
      });
      // Clear property limit error when removing
      setPropertyLimitError(null);
    }
  };

  const updateProperty = (index, field, value) => {
    setFormData((fd) => {
      const updated = [...fd.properties];
      updated[index] = { ...updated[index], [field]: value };
      return { ...fd, properties: updated };
    });
  };

  // Submit intake (submit → agreement acceptance → checkout; button stays disabled until request completes)
  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    if (agreementLoading) {
      setError('The service agreement is still loading. Please wait a moment.');
      return;
    }
    if (agreementFetchError || !agreementCurrent) {
      setError(
        agreementFetchError ||
          'The service agreement could not be loaded. Refresh the page or try again later.',
      );
      return;
    }
    if (!agreementRenderValidation.valid) {
      setError(
        'The service agreement could not be safely rendered. Please refresh and try again or contact support.',
      );
      return;
    }
    if (!serviceAgreementAccepted) {
      setError('Please confirm you have read and accept the service agreement before paying.');
      return;
    }
    setLoading(true);
    setError('');
    setErrorDetail(null);
    setShowLoginLink(false);
    const isDev = process.env.NODE_ENV !== 'production';
    try {
      const submitData = buildIntakeSubmitPayload(formData, intakeSessionId, marketing);
      if (isDev) {
        const base = typeof window !== 'undefined' && window.__CVP_BACKEND_URL;
        console.debug('[CVP] Intake submit → POST', base ? `${base}/api/intake/submit` : '/api/intake/submit');
        const bedroomTypes = (submitData.properties || []).map((p, i) => ({ i, bedrooms: p.bedrooms, type: typeof p.bedrooms }));
        console.debug('[CVP] Intake submit payload (bedrooms as number):', { bedroomTypes, fullPayload: submitData });
      }

      let clientId = resumeClientId;
      let customer_reference = null;
      if (!clientId) {
        const response = await intakeAPI.submit(submitData);
        clientId = response.data.client_id;
        customer_reference = response.data.customer_reference;
        if (isDev) console.debug('[CVP] Intake submit 200 client_id=', clientId);
        localStorage.setItem('pending_client_id', clientId);
        if (customer_reference) {
          localStorage.setItem('customer_reference', customer_reference);
        } else {
          localStorage.removeItem('customer_reference');
        }
        setResumeClientId(clientId);
      } else {
        customer_reference =
          typeof window !== 'undefined' ? window.localStorage.getItem('customer_reference') : null;
      }

      const authoritativePreview = await intakeAPI.previewAgreement({
        intake_session_id: intakeSessionId,
        client_id: clientId,
        invite_code: (pilotInviteCode || '').trim() || undefined,
        invite_entry_channel: pilotInviteEntryChannelFromSources(pilotInviteCode, inviteParam),
      });
      const pv = authoritativePreview.data;
      if (!pv?.render_hash_sha256) {
        setError(
          'The service agreement could not be verified for payment. Please refresh and try again or contact support.',
        );
        return;
      }
      const renderedForAcceptance = renderAgreementForDisplay(pv);
      if (!validateAgreementRender(renderedForAcceptance).valid) {
        setError(
          'The service agreement could not be safely rendered for payment. Please refresh and try again or contact support.',
        );
        return;
      }

      const acceptanceBody = {
        client_id: clientId,
        intake_session_id: intakeSessionId,
        template_code: pv.template_code || agreementCurrent.template_code,
        acceptance_text_snapshot: pv.acceptance_text_required || agreementCurrent.acceptance_text_required || '',
        accepted_by_name: (formData.full_name || '').trim(),
        accepted_by_email: canonicalIntakeEmail(formData.email) || (formData.email || '').trim(),
        document_submission_method: formData.document_submission_method || '',
        assisted_upload_consent_accepted:
          formData.document_submission_method === 'EMAIL' && !!formData.email_upload_consent,
        assisted_upload_consent_timestamp:
          formData.document_submission_method === 'EMAIL' && formData.email_upload_consent
            ? new Date().toISOString()
            : null,
        rendered_agreement_hash: pv.render_hash_sha256,
        rendered_agreement_snapshot: renderedForAcceptance,
      };
      const acceptancePilotCode = (pilotInviteCode || '').trim();
      if (acceptancePilotCode) {
        acceptanceBody.invite_code = acceptancePilotCode;
        acceptanceBody.invite_entry_channel = pilotInviteEntryChannelFromSources(pilotInviteCode, inviteParam);
      }
      const accRes = await publicAgreementsAPI.postAcceptance(acceptanceBody);
      const acceptance_id = accRes.data?.acceptance_id;
      if (!acceptance_id) {
        setError('Could not record agreement acceptance. Please refresh and try again.');
        return;
      }

      if (isDev) {
        const base = typeof window !== 'undefined' && window.__CVP_BACKEND_URL;
        console.debug(
          '[CVP] Intake checkout → POST',
          base ? `${base}/api/intake/checkout` : '/api/intake/checkout',
          'client_id=',
          clientId,
        );
      }
      const checkoutBody = { acceptance_id };
      const trimmedPilotCode = (pilotInviteCode || '').trim();
      if (trimmedPilotCode) {
        checkoutBody.invite_code = trimmedPilotCode;
        checkoutBody.invite_entry_channel = pilotInviteEntryChannelFromSources(pilotInviteCode, inviteParam);
      }
      const checkoutResponse = await intakeAPI.createCheckout(clientId, checkoutBody);
      const checkoutUrl = checkoutResponse?.data?.checkout_url;
      if (checkoutUrl) {
        if (isDev) console.debug('[CVP] Intake checkout 200 redirect →', checkoutUrl?.slice(0, 50) + '...');
        const crn =
          customer_reference ||
          (typeof window !== 'undefined' ? window.localStorage.getItem('customer_reference') : null);
        toast.success(
          pilotInviteValidation?.valid
            ? (pilotInviteValidation.headline || pilotInviteValidation.message || 'Founding pilot invite applied.')
            : crn
              ? `Opening secure payment. Your customer reference: ${crn}`
              : 'Opening secure payment…',
        );
        window.location.href = checkoutUrl;
        return;
      }
      const refId = checkoutResponse?.data?.request_id;
      setErrorDetail({ error_code: 'CHECKOUT_URL_MISSING', request_id: refId });
      setError(refId ? `Payment setup failed. Reference: ${refId}` : 'Payment setup failed. Please try again or contact support.');
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      const detail = data?.detail;
      // Parse message: string detail, object with .message, or 422 validation array (first error)
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object' && detail.message) {
        message = detail.message;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const msg = first?.msg ?? first?.message ?? JSON.stringify(first);
        const loc = Array.isArray(first?.loc) ? first.loc.filter(Boolean).join(' → ') : '';
        message = loc ? `Validation failed: ${msg} (${loc})` : `Validation failed: ${msg}`;
      } else if (data?.message) {
        message = data.message;
      } else if (!err.response) {
        message = 'Connection error. Please check your connection and try again.';
      } else {
        message = status ? `Request failed (${status}). Please try again or contact support.` : 'Failed to submit. Please try again.';
      }
      const code = typeof detail === 'object' && !Array.isArray(detail) ? detail?.error_code : null;
      const requestId = data?.request_id ?? (typeof detail === 'object' && !Array.isArray(detail) ? detail?.request_id : null);
      setErrorDetail(requestId || code ? { error_code: code, request_id: requestId } : null);
      if (code === 'PROPERTY_LIMIT_EXCEEDED') {
        setError(message || 'Property limit exceeded for your plan. Please reduce properties or choose a higher plan.');
      } else if (
        code === 'ACCEPTANCE_COMMERCIAL_MISMATCH' ||
        code === 'ACCEPTANCE_NOT_FOUND' ||
        code === 'ACCEPTANCE_NOT_VALID_FOR_CHECKOUT' ||
        code === 'ACCEPTANCE_CLIENT_MISMATCH' ||
        code === 'AGREEMENT_VERSION_NOT_PUBLISHED' ||
        code === 'AGREEMENT_TEMPLATE_INACTIVE' ||
        code === 'ACCEPTANCE_RENDER_INVALID'
      ) {
        setServiceAgreementAccepted(false);
        setError(
          message ||
            'Your details no longer match the agreement snapshot. Please read the agreement again, tick the box, and retry payment.',
        );
      } else if (code === 'INTAKE_SESSION_INVALID') {
        setServiceAgreementAccepted(false);
        setError(
          message ||
            'Your browser session does not match this registration. Refresh the page and start again, or contact support.',
        );
      } else if (code === 'ACCEPTANCE_CHECKOUT_LINK_FAILED') {
        setServiceAgreementAccepted(false);
        setError(
          message ||
            'Payment could not be linked to your agreement acceptance. Please confirm the agreement again and retry.',
        );
      } else if (status === 429 || code === 'RATE_LIMIT_EXCEEDED') {
        setError(message || 'Too many requests. Please wait a few minutes and try again.');
      } else if (
        code === 'PILOT_INVITE_INVALID' ||
        code === 'PILOT_INVITE_EXPIRED' ||
        code === 'PILOT_INVITE_EXHAUSTED' ||
        code === 'PILOT_INVITE_PLAN_NOT_ELIGIBLE' ||
        code === 'PILOT_INVITE_EMAIL_NOT_ELIGIBLE' ||
        code === 'PILOT_INVITE_MISCONFIGURED'
      ) {
        setError(message || 'This invite code could not be applied. Please check the code or contact support.');
      } else if (status === 402 || code === 'CHECKOUT_FAILED' || code === 'CHECKOUT_URL_MISSING') {
        setError(requestId
          ? `Payment setup failed. Reference: ${requestId}`
          : (message || 'Could not start payment. Please try again.'));
      } else {
        const duplicateEmail =
          status === 400 &&
          typeof message === 'string' &&
          (message === INTAKE_EMAIL_DUPLICATE_MESSAGE ||
            message.toLowerCase().includes('already exists'));
        if (duplicateEmail) {
          setEmailAvailability('taken');
          try {
            localStorage.removeItem('customer_reference');
            localStorage.removeItem('pending_client_id');
          } catch (_) {
            /* ignore */
          }
          setResumeClientId(null);
          setShowLoginLink(true);
        }
        if (requestId) {
          setError(
            message
              ? `${message} Support ID: ${requestId}`
              : `Request failed. Support ID: ${requestId}`,
          );
        } else {
          setError(message);
        }
      }
      if (isDev && (err.response?.data || !err.response)) {
        console.debug('[CVP] Intake error', status, detail ?? (err.response ? data : err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator
  const steps = [
    { num: 1, name: 'Your Details', icon: User },
    { num: 2, name: 'Select Plan', icon: CreditCard },
    { num: 3, name: 'Properties', icon: Home },
    { num: 4, name: 'Preferences', icon: FileText },
    { num: 5, name: 'Review', icon: Shield }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/90" data-testid="intake-wizard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={BRAND_LOGO_URL}
                alt=""
                className="h-10 w-auto max-h-10 max-w-[160px] object-contain object-left shrink-0"
                width={160}
                height={40}
                decoding="async"
              />
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-midnight-blue truncate">{branding.productName}</h1>
                <p className="text-xs text-slate-500">Premium UK Landlord Compliance</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => goToStep(s.num)}
                  disabled={s.num > step}
                  className={`flex flex-col items-center gap-1 transition-all ${
                    s.num <= step ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                  }`}
                  data-testid={`step-indicator-${s.num}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    s.num < step 
                      ? 'bg-green-500 text-white' 
                      : s.num === step 
                        ? 'bg-electric-teal text-white ring-4 ring-electric-teal/20' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s.num < step ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium ${
                    s.num === step ? 'text-electric-teal' : s.num < step ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {s.name}
                  </span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    s.num < step ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-10">
        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="intake-error-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span>{error}</span>
              {showLoginLink && (
                <p className="mt-2 text-sm">
                  <Link to="/login/client" className="font-medium text-electric-teal underline hover:no-underline">
                    Sign in to the client portal
                  </Link>{' '}
                  if you already have an account, or use a different email to register.
                </p>
              )}
              {errorDetail?.request_id && (
                <p className="mt-2 text-xs opacity-90">Reference: {errorDetail.request_id}</p>
              )}
              {errorDetail?.error_code && !errorDetail?.request_id && (
                <p className="mt-2 text-xs opacity-90">Code: {errorDetail.error_code}</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {prefillFromRiskCheck && (
          <Alert className="mb-6 border-blue-200 bg-blue-50" data-testid="intake-prefill-banner">
            <Info className="h-4 w-4" />
            <AlertDescription>
              {intakePlanRecommendation?.basis === 'property_count' ? (
                <>
                  We preloaded your details from your risk check and suggested a subscription tier from the property count
                  you reported. You can change anything.
                </>
              ) : (
                <>
                  We preloaded your details from your risk check. On the plan step, choose the tier that fits you—we
                  could not suggest one automatically from your saved answers. You can change anything.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Step 1: Your Details */}
        {step === 1 && (
          <Step1YourDetails
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            emailAvailability={emailAvailability}
            onEmailChange={handleStep1EmailChange}
            onEmailBlur={handleStep1EmailBlur}
            onRetryEmailCheck={() => {
              setError('');
              void runEmailAvailabilityCheck();
            }}
          />
        )}

        {/* Step 2: Select Plan */}
        {step === 2 && (
          <Step2SelectPlan
            formData={formData}
            setFormData={setFormData}
            plans={plans}
            onNext={nextStep}
            onBack={prevStep}
            intakePlanRecommendation={intakePlanRecommendation}
            prefillFromRiskCheck={prefillFromRiskCheck}
          />
        )}

        {/* Step 3: Properties */}
        {step === 3 && (
          <Step3Properties
            formData={formData}
            setFormData={setFormData}
            updateProperty={updateProperty}
            addProperty={addProperty}
            removeProperty={removeProperty}
            propertyLimitError={propertyLimitError}
            setPropertyLimitError={setPropertyLimitError}
            onNext={nextStep}
            onBack={prevStep}
            goToStep={goToStep}
            leadPropertyCountHint={leadPropertyCountHint}
          />
        )}

        {/* Step 4: Preferences & Consents */}
        {step === 4 && (
          <Step4Preferences
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onBack={prevStep}
            intakeSessionId={intakeSessionId}
          />
        )}

        {/* Step 5: Review & Payment */}
        {step === 5 && (
          <Step5Review
            formData={formData}
            plans={plans}
            goToStep={goToStep}
            onSubmit={handleSubmit}
            onBack={prevStep}
            loading={loading}
            intakeSessionId={intakeSessionId}
            agreementCurrent={agreementCurrent}
            agreementLoading={agreementLoading}
            agreementError={agreementFetchError}
            agreementDiagnostic={agreementFetchDiagnostic}
            onRetryAgreementPreview={() => setAgreementRetryNonce((n) => n + 1)}
            renderedAgreement={renderedAgreement}
            agreementRenderValidation={agreementRenderValidation}
            serviceAgreementAccepted={serviceAgreementAccepted}
            onServiceAgreementAcceptedChange={setServiceAgreementAccepted}
            pilotInviteCode={pilotInviteCode}
            onPilotInviteCodeChange={setPilotInviteCode}
            pilotInviteValidation={pilotInviteValidation}
            pilotInviteValidating={pilotInviteValidating}
            onValidatePilotInvite={validatePilotInviteForPlan}
          />
        )}
      </main>
    </div>
  );
};

// ============================================================================
// STEP 1: YOUR DETAILS
// ============================================================================
const Step1YourDetails = ({
  formData,
  setFormData,
  onNext,
  emailAvailability,
  onEmailChange,
  onEmailBlur,
  onRetryEmailCheck,
}) => {
  const clientTypes = [
    { value: 'INDIVIDUAL', label: 'Individual Landlord', icon: User, desc: 'Managing your own properties' },
    { value: 'COMPANY', label: 'Property Company', icon: Building2, desc: 'Corporate property management' },
    { value: 'AGENT', label: 'Letting Agent', icon: Users, desc: 'Managing properties for others' }
  ];

  const showCompanyName = formData.client_type === 'COMPANY' || formData.client_type === 'AGENT';
  const showPhone = formData.preferred_contact === 'SMS' || formData.preferred_contact === 'BOTH';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl text-midnight-blue">Your Details</CardTitle>
        <CardDescription>Tell us about yourself to personalize your experience</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Full Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Full Name *</label>
          <Input
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            placeholder="John Smith"
            data-testid="full-name-input"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Email Address *</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={onEmailBlur}
            placeholder="john@example.com"
            data-testid="email-input"
            autoComplete="email"
          />
          {emailAvailability === 'checking' && (
            <p
              className="text-xs text-gray-600 flex items-center gap-1.5"
              data-testid="email-availability-checking"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
              Checking email…
            </p>
          )}
          {emailAvailability === 'available' && (
            <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="email-availability-available">
              <CheckCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Email available
            </p>
          )}
          {emailAvailability === 'taken' && (
            <div className="text-xs text-red-700 space-y-2" data-testid="email-availability-taken">
              <p className="flex items-start gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                <span>{INTAKE_EMAIL_DUPLICATE_MESSAGE}</span>
              </p>
              <p>
                <Link to="/login/client" className="font-medium text-electric-teal underline hover:no-underline">
                  Sign in to the client portal
                </Link>
                <span className="text-gray-500"> · </span>
                <Link to="/forgot-password" className="font-medium text-electric-teal underline hover:no-underline">
                  Forgot password
                </Link>
              </p>
            </div>
          )}
          {emailAvailability === 'error' && (
            <div className="text-xs text-slate-800 space-y-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="email-availability-error">
              <p>Could not verify email right now.</p>
              <button
                type="button"
                onClick={onRetryEmailCheck}
                className="font-medium text-electric-teal underline hover:no-underline"
                data-testid="email-availability-retry"
              >
                Retry check
              </button>
            </div>
          )}
        </div>

        {/* Client Type - Card Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">I am a... *</label>
          <div className="grid grid-cols-3 gap-3">
            {clientTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, client_type: type.value })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.client_type === type.value
                    ? 'border-electric-teal bg-electric-teal/5 ring-2 ring-electric-teal/20'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`client-type-${type.value.toLowerCase()}`}
              >
                <type.icon className={`w-6 h-6 mb-2 ${
                  formData.client_type === type.value ? 'text-electric-teal' : 'text-gray-400'
                }`} />
                <p className="font-medium text-sm text-midnight-blue">{type.label}</p>
                <p className="text-xs text-gray-500 mt-1">{type.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Company Name (Conditional) */}
        {showCompanyName && (
          <div className="space-y-2 animate-fadeIn">
            <label className="text-sm font-medium text-gray-700">Company Name *</label>
            <Input
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="ABC Properties Ltd"
              data-testid="company-name-input"
            />
          </div>
        )}

        {/* Preferred Contact Method */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Preferred Contact Method *</label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'EMAIL', label: 'Email Only' },
              { value: 'SMS', label: 'SMS Only' },
              { value: 'BOTH', label: 'Both Email & SMS' }
            ].map((method) => (
              <button
                key={method.value}
                type="button"
                onClick={() => setFormData({ ...formData, preferred_contact: method.value })}
                className={`py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                  formData.preferred_contact === method.value
                    ? 'border-electric-teal bg-electric-teal/5 text-electric-teal'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid={`contact-${method.value.toLowerCase()}`}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* Phone Number (Conditional) */}
        {showPhone && (
          <div className="space-y-2 animate-fadeIn">
            <label className="text-sm font-medium text-gray-700">Phone Number *</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+44 7700 900000"
              data-testid="phone-input"
            />
            <p className="text-xs text-gray-500">Required for SMS notifications</p>
          </div>
        )}

        <div className="pt-4">
          <Button
            onClick={onNext}
            className="w-full"
            data-testid="step1-next"
            disabled={emailAvailability === 'checking'}
          >
            {emailAvailability === 'checking' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                Checking email…
              </>
            ) : (
              <>
                Next: Select Plan
                <ChevronRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// STEP 2: SELECT PLAN
// ============================================================================
const Step2SelectPlan = ({
  formData,
  setFormData,
  plans,
  onNext,
  onBack,
  intakePlanRecommendation,
  prefillFromRiskCheck,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl text-midnight-blue">Select Your Plan</CardTitle>
        <CardDescription>
          {intakePlanRecommendation?.basis === 'property_count' ? (
            <>We suggested a plan from your risk check answers. You can change anything below.</>
          ) : prefillFromRiskCheck ? (
            <>
              Choose the plan that fits your portfolio. We could not auto-suggest a tier from your risk check (missing
              or unclear property count).
            </>
          ) : (
            <>Choose the plan that fits your portfolio size</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {intakePlanRecommendation?.basis === 'property_count' && (
          <Alert className="border-slate-200 bg-slate-50 text-slate-800" role="status" data-testid="intake-plan-recommendation-note">
            <Info className="h-4 w-4 text-midnight-blue" />
            <AlertDescription>
              Recommended based on your reported property count
              {typeof intakePlanRecommendation.propertyCount === 'number' &&
              !Number.isNaN(intakePlanRecommendation.propertyCount)
                ? ` (${intakePlanRecommendation.propertyCount}).`
                : '.'}{' '}
              This is a suggestion only — pick any plan that fits you.
            </AlertDescription>
          </Alert>
        )}
        <div className="grid gap-4">
          {plans.map((plan) => (
            <button
              key={plan.plan_id}
              type="button"
              onClick={() => setFormData({ ...formData, billing_plan: plan.plan_id })}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                formData.billing_plan === plan.plan_id
                  ? 'border-electric-teal bg-electric-teal/5 ring-2 ring-electric-teal/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`plan-${plan.plan_id.toLowerCase().replace(/_/g, '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-midnight-blue">{plan.display_name || plan.name}</h3>
                    {intakePlanRecommendation?.basis === 'property_count' &&
                      intakePlanRecommendation.planCode === plan.plan_id && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium border border-electric-teal/40 bg-white text-midnight-blue">
                          Suggested from risk check
                        </span>
                      )}
                    {plan.is_popular && (
                      <span className="text-xs px-2 py-0.5 bg-electric-teal/10 text-midnight-blue rounded-full font-medium border border-electric-teal/20">
                        {plan.badge || 'Popular'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Up to {plan.max_properties} {plan.max_properties === 1 ? 'property' : 'properties'}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-electric-teal">£{plan.monthly_price.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">/month</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <ul className="grid grid-cols-2 gap-2">
                  {plan.features?.slice(0, 6).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {plan.features?.length > 6 && (
                  <p className="text-xs text-gray-400 mt-2">+{plan.features.length - 6} more features</p>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">First payment:</span>
                <span className="font-semibold text-midnight-blue">
                  £{(plan.monthly_price + plan.setup_fee).toFixed(2)}
                  <span className="text-xs text-gray-500 ml-1">(incl. £{plan.setup_fee.toFixed(2)} setup)</span>
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1" data-testid="step2-back">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            onClick={onNext} 
            className="flex-1"
            data-testid="step2-next"
          >
            Next: Add Properties
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// STEP 3: PROPERTIES
// ============================================================================
const Step3Properties = ({ formData, setFormData, updateProperty, addProperty, removeProperty, propertyLimitError, setPropertyLimitError, onNext, onBack, goToStep, leadPropertyCountHint }) => {
  const { usageContext } = usePortfolioUsageContext();
  const existingPortfolioCount =
    typeof usageContext?.property_count === 'number' ? usageContext.property_count : 0;
  const maxProperties = PLAN_LIMITS[formData.billing_plan] ?? 2;
  const currentCount = formData.properties.length;
  const canAddMore = currentCount < maxProperties;

  // Get upgrade plan info (single source: formData.billing_plan + PLAN_LIMITS)
  const getUpgradePlan = () => {
    const plan = formData.billing_plan;
    if (plan === 'PLAN_1_SOLO' || plan === 'PLAN_1') {
      return { code: 'PLAN_2_PORTFOLIO', name: 'Portfolio', limit: 10 };
    } else if (plan === 'PLAN_2_PORTFOLIO' || plan === 'PLAN_2_5') {
      return { code: 'PLAN_3_PRO', name: 'Professional', limit: 25 };
    }
    return null;
  };

  const upgradePlan = getUpgradePlan();

  const handleUpgrade = () => {
    if (upgradePlan) {
      setFormData({ ...formData, billing_plan: upgradePlan.code });
      setPropertyLimitError(null);
      if (goToStep) goToStep(2);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-midnight-blue">Your Properties</CardTitle>
              <CardDescription>
                Add up to {maxProperties} {maxProperties === 1 ? 'property' : 'properties'} with the {PLAN_NAMES[formData.billing_plan] || 'current'} plan
              </CardDescription>
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              formData.properties.length >= maxProperties 
                ? 'bg-slate-200 text-slate-800' 
                : 'bg-gray-100 text-gray-700'
            }`}>
              {formData.properties.length}/{maxProperties}
            </span>
          </div>
          {leadPropertyCountHint != null && leadPropertyCountHint > 1 && (
            <p className="text-sm text-blue-700 mt-2">You indicated {leadPropertyCountHint} properties; add them here.</p>
          )}
          {existingPortfolioCount > 0 && (
            <p
              className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 mt-3 leading-relaxed"
              data-testid="intake-existing-properties-hint"
            >
              Your account already has <strong>{existingPortfolioCount}</strong> propert
              {existingPortfolioCount === 1 ? 'y' : 'ies'} on file. The plan you select limits your{' '}
              <strong>total</strong> portfolio — include those properties when choosing how many to add here.
            </p>
          )}
        </CardHeader>
      </Card>

      {/* Property Limit Error with Upgrade Prompt */}
      {propertyLimitError && (
        <PropertyLimitPrompt
          currentLimit={propertyLimitError.currentLimit}
          requestedCount={propertyLimitError.requestedCount}
          currentPlan={propertyLimitError.currentPlan}
          upgradePlan={propertyLimitError.upgradePlan}
          upgradePlanName={propertyLimitError.upgradePlanName}
          upgradeLimit={propertyLimitError.upgradeLimit}
          onUpgrade={handleUpgrade}
          switchPlanOnly
        />
      )}

      {formData.properties.map((property, index) => (
        <PropertyCard
          key={index}
          property={property}
          index={index}
          total={formData.properties.length}
          updateProperty={updateProperty}
          removeProperty={removeProperty}
          showLicenceFields={property.licence_required === 'YES'}
          showAgentFields={property.send_reminders_to === 'AGENT' || property.send_reminders_to === 'BOTH'}
        />
      ))}

      {/* Add Property Button – disabled at cap so no add path can exceed limit */}
      {canAddMore ? (
        <button
          type="button"
          onClick={addProperty}
          disabled={!canAddMore}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-electric-teal hover:text-electric-teal transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
          data-testid="add-property-btn"
        >
          <Plus className="w-5 h-5" />
          Add Another Property ({currentCount + 1}/{maxProperties})
        </button>
      ) : (
        <div className="p-4 sm:p-5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 border-l-4 border-l-brand-warning" data-testid="property-limit-warning">
          <Lock className="w-5 h-5 text-brand-warning shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-midnight-blue">Property limit reached</p>
            <p className="text-sm text-slate-600 leading-relaxed mt-1">
              You've added the maximum {maxProperties} {maxProperties === 1 ? 'property' : 'properties'} for the {PLAN_NAMES[formData.billing_plan] || 'current'} plan.
              {upgradePlan && (
                <>
                  {' '}
                  <button 
                    onClick={handleUpgrade}
                    className="underline font-medium text-electric-teal hover:text-electric-teal/90"
                    data-testid="upgrade-plan-link"
                  >
                    Upgrade to {upgradePlan.name} (up to {upgradePlan.limit} properties)
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" data-testid="step3-back">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={onNext} 
          className="flex-1"
          data-testid="step3-next"
        >
          Next: Preferences
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Property Card Component
const PropertyCard = ({ property, index, total, updateProperty, removeProperty, showLicenceFields, showAgentFields }) => {
  const [councilSearch, setCouncilSearch] = useState('');
  const [councilResults, setCouncilResults] = useState([]);
  const [showCouncilDropdown, setShowCouncilDropdown] = useState(false);
  const [loadingCouncils, setLoadingCouncils] = useState(false);

  const councilRef = useRef(null);

  const postcodeLookup = useUkPostcodeLookup({
    postcode: property.postcode,
    onPostcodeChange: (postcode) => updateProperty(index, 'postcode', postcode),
    onLookupComplete: (data) => {
      if (data.suggested_city && !property.city) {
        updateProperty(index, 'city', data.suggested_city);
      }
      if (data.council_name && !property.council_name) {
        updateProperty(index, 'council_name', data.council_name);
        updateProperty(index, 'council_code', data.council_code);
        setCouncilSearch(data.council_name);
      }
    },
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (councilRef.current && !councilRef.current.contains(e.target)) {
        setShowCouncilDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search councils
  const searchCouncils = useCallback(async (query) => {
    if (query.length < 2) {
      setCouncilResults([]);
      return;
    }
    
    setLoadingCouncils(true);
    try {
      const response = await intakeAPI.searchCouncils(query);
      setCouncilResults(response.data.councils);
    } catch (err) {
      console.error('Council search error:', err);
    } finally {
      setLoadingCouncils(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (councilSearch) {
        searchCouncils(councilSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [councilSearch, searchCouncils]);

  const selectCouncil = (council) => {
    updateProperty(index, 'council_name', council.name);
    updateProperty(index, 'council_code', council.code);
    setCouncilSearch(council.name);
    setShowCouncilDropdown(false);
  };

  const propertyPostcodeLookupContext = {
    city: property.city,
    jurisdiction: property.jurisdiction,
  };

  return (
    <Card className="overflow-hidden" data-testid={`property-card-${index}`}>
      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Home className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-midnight-blue">{getPropertyDisplayName(property)}</span>
        </div>
        {total > 1 && (
          <button
            type="button"
            onClick={() => removeProperty(index)}
            className="text-red-500 hover:text-red-600 p-1"
            data-testid={`remove-property-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      
      <CardContent className="pt-6 space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Property Nickname</label>
            <Input
              value={property.nickname}
              onChange={(e) => updateProperty(index, 'nickname', e.target.value)}
              placeholder="e.g., Main Street Flat"
              data-testid={`property-${index}-nickname`}
            />
          </div>
          <UkPostcodeLookupField
            postcodeRef={postcodeLookup.postcodeRef}
            postcodeInput={postcodeLookup.postcodeInput}
            onPostcodeChange={postcodeLookup.handlePostcodeChange}
            onPostcodeFocus={() =>
              postcodeLookup.postcodeInput.length >= 2 && postcodeLookup.setShowPostcodeDropdown(true)
            }
            onPostcodeBlur={() => postcodeLookup.handlePostcodeBlur(propertyPostcodeLookupContext)}
            postcodeSuggestions={postcodeLookup.postcodeSuggestions}
            showPostcodeDropdown={postcodeLookup.showPostcodeDropdown}
            loadingPostcodes={postcodeLookup.loadingPostcodes}
            lookingUpPostcode={postcodeLookup.lookingUpPostcode}
            postcodeLookupDone={postcodeLookup.postcodeLookupDone}
            postcodeError={postcodeLookup.postcodeError}
            onSelectSuggestion={(s) => postcodeLookup.selectPostcode(s, propertyPostcodeLookupContext)}
            testId={`property-${index}-postcode`}
            lookupDoneMessage="City and council auto-filled ✓"
            hintMessage="Select from suggestions or type full postcode"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Address Line 1 *</label>
          <Input
            value={property.address_line_1}
            onChange={(e) => updateProperty(index, 'address_line_1', e.target.value)}
            placeholder="123 Example Street"
            data-testid={`property-${index}-address1`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Address Line 2</label>
            <Input
              value={property.address_line_2}
              onChange={(e) => updateProperty(index, 'address_line_2', e.target.value)}
              placeholder="Flat 4, Building Name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">City *</label>
            <Input
              value={property.city}
              onChange={(e) => updateProperty(index, 'city', e.target.value)}
              placeholder="London"
              data-testid={`property-${index}-city`}
            />
          </div>
        </div>

        {/* Property Type & Details */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Property Type</label>
            <select
              value={property.property_type}
              onChange={(e) => updateProperty(index, 'property_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              data-testid={`property-${index}-type`}
            >
              {PROPERTY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Bedrooms</label>
            <Input
              type="number"
              min="1"
              max="20"
              value={property.bedrooms}
              onChange={(e) => updateProperty(index, 'bedrooms', e.target.value)}
              placeholder="3"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Occupancy</label>
            <select
              value={property.occupancy}
              onChange={(e) => updateProperty(index, 'occupancy', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {OCCUPANCY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Jurisdiction *</label>
            <select
              value={property.jurisdiction || ''}
              onChange={(e) => updateProperty(index, 'jurisdiction', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
              data-testid={`property-${index}-jurisdiction`}
            >
              <option value="">Select jurisdiction...</option>
              {JURISDICTION_OPTIONS.map((j) => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Gas supply</label>
            <select
              value={property.has_gas_supply === null || property.has_gas_supply === undefined ? '' : String(property.has_gas_supply)}
              onChange={(e) => {
                const v = e.target.value;
                updateProperty(index, 'has_gas_supply', v === '' ? null : v === 'true');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
              data-testid={`property-${index}-gas-supply`}
            >
              <option value="">Not sure yet</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        {/* HMO Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-sm text-midnight-blue">Is this an HMO?</p>
            <p className="text-xs text-gray-500">House in Multiple Occupation</p>
          </div>
          <Switch
            checked={property.is_hmo}
            onCheckedChange={(checked) => updateProperty(index, 'is_hmo', checked)}
            data-testid={`property-${index}-hmo`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm text-midnight-blue">Active tenancy?</p>
              <p className="text-xs text-gray-500">Drives tenancy-specific obligations</p>
            </div>
            <Switch
              checked={!!property.tenancy_active}
              onCheckedChange={(checked) => updateProperty(index, 'tenancy_active', checked)}
              data-testid={`property-${index}-tenancy-active`}
            />
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm text-midnight-blue">Deposit taken?</p>
              <p className="text-xs text-gray-500">Used for deposit prescribed info</p>
            </div>
            <Switch
              checked={!!property.deposit_taken}
              onCheckedChange={(checked) => updateProperty(index, 'deposit_taken', checked)}
              data-testid={`property-${index}-deposit-taken`}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Furnished</label>
            <select
              value={property.furnished === null || property.furnished === undefined ? '' : String(property.furnished)}
              onChange={(e) => {
                const v = e.target.value;
                updateProperty(index, 'furnished', v === '' ? null : v === 'true');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
              data-testid={`property-${index}-furnished`}
            >
              <option value="">Not sure yet</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm text-midnight-blue">Has communal areas?</p>
              <p className="text-xs text-gray-500">Used for communal-area requirements</p>
            </div>
            <Switch
              checked={!!property.has_communal_areas}
              onCheckedChange={(checked) => updateProperty(index, 'has_communal_areas', checked)}
              data-testid={`property-${index}-communal`}
            />
          </div>
        </div>

        {/* Council Search */}
        <div className="space-y-2" ref={councilRef}>
          <label className="text-sm font-medium text-gray-700">Local Council</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={councilSearch || property.council_name}
              onChange={(e) => {
                setCouncilSearch(e.target.value);
                setShowCouncilDropdown(true);
              }}
              onFocus={() => setShowCouncilDropdown(true)}
              placeholder="Search councils..."
              className="pl-10"
              data-testid={`property-${index}-council`}
            />
            {property.council_name && (
              <button
                type="button"
                onClick={() => {
                  updateProperty(index, 'council_name', '');
                  updateProperty(index, 'council_code', '');
                  setCouncilSearch('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          
          {showCouncilDropdown && councilResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {councilResults.map((council) => (
                <button
                  key={council.code}
                  type="button"
                  onClick={() => selectCouncil(council)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="font-medium text-sm">{council.name}</span>
                  <span className="text-xs text-gray-500">{council.region}</span>
                </button>
              ))}
            </div>
          )}
          {loadingCouncils && (
            <p className="text-xs text-gray-500">Searching...</p>
          )}
        </div>

        {/* Licensing */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Is a licence required?</label>
            <div className="flex gap-2">
              {['YES', 'NO', 'UNSURE'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => updateProperty(index, 'licence_required', opt)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    property.licence_required === opt
                      ? 'bg-electric-teal text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {opt === 'YES' ? 'Yes' : opt === 'NO' ? 'No' : 'Unsure'}
                </button>
              ))}
            </div>
          </div>

          {showLicenceFields && (
            <div className="grid grid-cols-2 gap-4 pt-2 animate-fadeIn">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Licence Type</label>
                <select
                  value={property.licence_type}
                  onChange={(e) => updateProperty(index, 'licence_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select type...</option>
                  {LICENCE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Licence Status</label>
                <select
                  value={property.licence_status}
                  onChange={(e) => updateProperty(index, 'licence_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select status...</option>
                  {LICENCE_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Management & Reminders */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Who manages this property?</label>
            <select
              value={property.managed_by}
              onChange={(e) => updateProperty(index, 'managed_by', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="LANDLORD">Landlord</option>
              <option value="AGENT">Agent</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Send reminders to</label>
            <select
              value={property.send_reminders_to}
              onChange={(e) => updateProperty(index, 'send_reminders_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="LANDLORD">Landlord</option>
              <option value="AGENT">Agent</option>
              <option value="BOTH">Both</option>
            </select>
          </div>
        </div>

        {/* Agent Details (Conditional) */}
        {showAgentFields && (
          <div className="p-4 bg-blue-50 rounded-lg space-y-4 animate-fadeIn">
            <p className="text-sm font-medium text-blue-800">Agent Details</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Agent Name *</label>
                <Input
                  value={property.agent_name}
                  onChange={(e) => updateProperty(index, 'agent_name', e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Agent Email *</label>
                <Input
                  type="email"
                  value={property.agent_email}
                  onChange={(e) => updateProperty(index, 'agent_email', e.target.value)}
                  placeholder="agent@example.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Agent Phone</label>
                <Input
                  type="tel"
                  value={property.agent_phone}
                  onChange={(e) => updateProperty(index, 'agent_phone', e.target.value)}
                  placeholder="+44 7700 900000"
                />
              </div>
            </div>
          </div>
        )}

        {/* Current Compliance Status — operational snapshot (same fields / validation as before) */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 sm:px-5 sm:py-4 bg-slate-50/90 border-b border-slate-200 flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-electric-teal/10 text-electric-teal">
              <Shield className="w-5 h-5" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold text-midnight-blue tracking-tight">Current Compliance Status</p>
              <p className="text-sm text-slate-600 leading-relaxed">Do you currently have these certificates?</p>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            {[
              { key: 'cert_gas_safety', label: 'Gas Safety Certificate' },
              { key: 'cert_eicr', label: 'EICR (Electrical)' },
              { key: 'cert_epc', label: 'EPC' },
              ...(property.licence_required === 'YES' ? [{ key: 'cert_licence', label: 'Licence' }] : [])
            ].map(cert => (
              <div key={cert.key} className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{cert.label}</label>
                <select
                  value={property[cert.key]}
                  onChange={(e) => updateProperty(index, cert.key, e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-md bg-white text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-electric-teal/30 focus:border-electric-teal"
                >
                  <option value="">Select...</option>
                  {CERT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// STEP 4: PREFERENCES & CONSENTS
// ============================================================================
const Step4Preferences = ({ formData, setFormData, onNext, onBack, intakeSessionId }) => {
  const PLEERITY_EMAIL = 'info@pleerityenterprise.co.uk';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl text-midnight-blue">Preferences & Consents</CardTitle>
        <CardDescription>Choose how you'd like to submit documents and accept required terms</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Document Submission Method */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-gray-700">How would you like to submit documents? *</label>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Option A: Upload */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, document_submission_method: 'UPLOAD', email_upload_consent: false }))}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                formData.document_submission_method === 'UPLOAD'
                  ? 'border-electric-teal bg-electric-teal/5 ring-2 ring-electric-teal/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="doc-method-upload"
            >
              <Upload className={`w-8 h-8 mb-3 ${
                formData.document_submission_method === 'UPLOAD' ? 'text-electric-teal' : 'text-gray-400'
              }`} />
              <h3 className="font-semibold text-midnight-blue">Upload Here</h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload your compliance documents directly through the portal
              </p>
            </button>
            
            {/* Option B: Email */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, document_submission_method: 'EMAIL' }))}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                formData.document_submission_method === 'EMAIL'
                  ? 'border-electric-teal bg-electric-teal/5 ring-2 ring-electric-teal/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="doc-method-email"
            >
              <Mail className={`w-8 h-8 mb-3 ${
                formData.document_submission_method === 'EMAIL' ? 'text-electric-teal' : 'text-gray-400'
              }`} />
              <h3 className="font-semibold text-midnight-blue">Email to Pleerity</h3>
              <p className="text-sm text-gray-500 mt-1">
                Email your documents and we'll upload them for you
              </p>
            </button>
          </div>
        </div>

        {/* Upload Here Section: only when UPLOAD; EMAIL path has no dropzone, no file requirement */}
        {formData.document_submission_method === 'UPLOAD' && (
          <IntakeDocumentUpload 
            intakeSessionId={intakeSessionId}
            onFilesChange={(files) => setFormData(prev => ({ ...prev, uploaded_files: files }))}
          />
        )}

        {/* Email to Pleerity Section */}
        {formData.document_submission_method === 'EMAIL' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4 animate-fadeIn">
            <div>
              <p className="text-sm font-medium text-blue-800 mb-2">Send your documents to:</p>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
                <Mail className="w-5 h-5 text-blue-600" />
                <span className="font-mono font-medium text-blue-900">{PLEERITY_EMAIL}</span>
              </div>
              <p className="text-sm text-blue-800 mt-2">
                <strong>Subject:</strong> Include your full name and customer reference (you will receive this after payment).
              </p>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Body:</strong> Attach your compliance documents (PDF, JPG, PNG, or DOCX). We will upload them into your portal.
              </p>
              <p className="text-xs text-blue-700 mt-2">
                You can also add or upload documents later from your portal after account setup.
              </p>
            </div>
            
            <label className="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-200 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.email_upload_consent}
                onChange={(e) => setFormData(prev => ({ ...prev, email_upload_consent: e.target.checked }))}
                className="mt-1 rounded border-gray-300 text-electric-teal focus:ring-electric-teal"
                data-testid="email-consent-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  I consent to Pleerity uploading documents on my behalf *
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  By checking this box, you authorise Pleerity Enterprise Ltd to upload compliance documents you email to {PLEERITY_EMAIL} into your portal.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Mandatory Consents */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="font-semibold text-midnight-blue">Required Consents</h3>
          
          {/* GDPR Consent */}
          <label className="flex items-start gap-3 p-4 sm:p-5 rounded-xl border border-slate-200 bg-slate-50/60 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.consent_data_processing}
              onChange={(e) => setFormData({ ...formData, consent_data_processing: e.target.checked })}
              className="mt-1 rounded border-slate-300 text-electric-teal focus:ring-electric-teal"
              data-testid="gdpr-consent-checkbox"
            />
            <div>
              <span className="text-sm font-medium text-midnight-blue">
                Data Processing Consent (GDPR) *
              </span>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                I consent to Pleerity Enterprise Ltd processing my personal data for compliance management purposes in accordance with their Privacy Policy.
              </p>
            </div>
          </label>

          {/* Service Boundary Acknowledgment */}
          <label className="flex items-start gap-3 p-4 sm:p-5 rounded-xl border border-slate-200 bg-slate-50/60 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.consent_service_boundary}
              onChange={(e) => setFormData({ ...formData, consent_service_boundary: e.target.checked })}
              className="mt-1 rounded border-slate-300 text-electric-teal focus:ring-electric-teal"
              data-testid="service-consent-checkbox"
            />
            <div>
              <span className="text-sm font-medium text-midnight-blue">
                Service Acknowledgment *
              </span>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                I understand that Compliance Vault Pro does not provide legal advice or guarantee regulatory compliance. The service is a compliance tracking tool and the responsibility for ensuring regulatory compliance remains with the property owner/manager.
              </p>
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1" data-testid="step4-back">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            onClick={onNext} 
            className="flex-1"
            data-testid="step4-next"
          >
            Review & Pay
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// STEP 5: REVIEW & PAYMENT
// ============================================================================
const Step5Review = ({
  formData,
  plans,
  goToStep,
  onSubmit,
  onBack,
  loading,
  intakeSessionId,
  agreementCurrent,
  agreementLoading,
  agreementError,
  agreementDiagnostic,
  onRetryAgreementPreview,
  renderedAgreement,
  agreementRenderValidation,
  serviceAgreementAccepted,
  onServiceAgreementAcceptedChange,
  pilotInviteCode,
  onPilotInviteCodeChange,
  pilotInviteValidation,
  pilotInviteValidating,
  onValidatePilotInvite,
}) => {
  const selectedPlan = plans.find(p => p.plan_id === formData.billing_plan);
  const pilotPricingActive =
    pilotInviteValidation?.valid && pilotInviteValidation?.plan_code === formData.billing_plan;
  const effectiveSetupFee = pilotPricingActive
    ? (pilotInviteValidation.onboarding_fee_waived ? 0 : (pilotInviteValidation.setup_fee_effective ?? selectedPlan?.setup_fee ?? 0))
    : (selectedPlan?.setup_fee ?? 0);
  const totalDueToday = pilotPricingActive
    ? (pilotInviteValidation.first_payment_estimate ?? effectiveSetupFee + (selectedPlan?.monthly_price ?? 0))
    : ((selectedPlan?.monthly_price ?? 0) + (selectedPlan?.setup_fee ?? 0));
  const [stagedFiles, setStagedFiles] = useState([]);
  const [showAgreementViewer, setShowAgreementViewer] = useState(false);
  const [requirementsPreview, setRequirementsPreview] = useState([]);
  const [requirementsPreviewLoading, setRequirementsPreviewLoading] = useState(false);
  const [requirementsPreviewError, setRequirementsPreviewError] = useState('');
  const maxProps = PLAN_LIMITS[formData.billing_plan] || 2;
  const agreementBlocksPayment =
    agreementLoading ||
    Boolean(agreementError) ||
    !agreementCurrent ||
    !agreementRenderValidation?.valid ||
    !serviceAgreementAccepted;

  useEffect(() => {
    if (formData.document_submission_method !== 'UPLOAD' || !intakeSessionId) {
      setStagedFiles([]);
      return;
    }
    const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
    const apiUrl = raw ? `${raw}/api` : '/api';
    fetch(`${apiUrl}/intake/uploads/list/${intakeSessionId}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setStagedFiles(Array.isArray(list) ? list : []))
      .catch(() => setStagedFiles([]));
  }, [intakeSessionId, formData.document_submission_method]);

  useEffect(() => {
    let cancelled = false;
    const loadPreview = async () => {
      setRequirementsPreviewLoading(true);
      setRequirementsPreviewError('');
      try {
        const res = await intakeAPI.previewRequirements(formData.properties || []);
        if (!cancelled) {
          setRequirementsPreview(Array.isArray(res.data?.properties) ? res.data.properties : []);
        }
      } catch (err) {
        if (!cancelled) {
          setRequirementsPreview([]);
          setRequirementsPreviewError(
            typeof err?.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not generate compliance summary preview'
          );
        }
      } finally {
        if (!cancelled) setRequirementsPreviewLoading(false);
      }
    };
    if ((formData.properties || []).length > 0) loadPreview();
    return () => { cancelled = true; };
  }, [formData.properties]);

  const clientTypeLabels = {
    INDIVIDUAL: 'Individual Landlord',
    COMPANY: 'Property Company',
    AGENT: 'Letting Agent'
  };

  const renderAgreementNodes = (section) => (
    <div className="space-y-2">
      {(section?.nodes || []).map((node, idx) => {
        if (node?.type === 'subheading') {
          return <p key={`${section.key}-sub-${idx}`} className="font-semibold text-gray-800">{node.text}</p>;
        }
        if (node?.type === 'bullet_list') {
          return (
            <ul key={`${section.key}-list-${idx}`} className="list-disc pl-6 space-y-1 text-sm leading-relaxed text-gray-700">
              {(node.items || []).map((item, i) => <li key={`${section.key}-li-${i}`}>{item}</li>)}
            </ul>
          );
        }
        return <p key={`${section.key}-p-${idx}`} className="text-sm leading-relaxed text-gray-700">{node?.text || ''}</p>;
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-midnight-blue">Review Your Details</CardTitle>
          <CardDescription>Please review your information before proceeding to payment</CardDescription>
        </CardHeader>
      </Card>

      {/* Your Details Summary */}
      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="font-semibold text-midnight-blue">Your Details</h3>
          <button 
            onClick={() => goToStep(1)} 
            className="text-sm text-electric-teal hover:underline"
            data-testid="edit-details"
          >
            Edit
          </button>
        </div>
        <CardContent className="pt-4">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Full Name</dt>
              <dd className="font-medium text-midnight-blue">{formData.full_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-midnight-blue">{formData.email}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Account Type</dt>
              <dd className="font-medium text-midnight-blue">{clientTypeLabels[formData.client_type]}</dd>
            </div>
            {formData.company_name && (
              <div>
                <dt className="text-gray-500">Company</dt>
                <dd className="font-medium text-midnight-blue">{formData.company_name}</dd>
              </div>
            )}
            <div>
              <dt className="text-gray-500">Contact Preference</dt>
              <dd className="font-medium text-midnight-blue">{formData.preferred_contact}</dd>
            </div>
            {formData.phone && (
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd className="font-medium text-midnight-blue">{formData.phone}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Plan Summary */}
      {selectedPlan && (
        <Card>
          <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
            <h3 className="font-semibold text-midnight-blue">Selected Plan</h3>
            <button 
              onClick={() => goToStep(2)} 
              className="text-sm text-electric-teal hover:underline"
              data-testid="edit-plan"
            >
              Edit
            </button>
          </div>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-midnight-blue">{selectedPlan.name}</p>
                <p className="text-sm text-gray-500">Up to {selectedPlan.max_properties} properties</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-electric-teal">£{selectedPlan.monthly_price.toFixed(2)}/month</p>
                <p className="text-xs text-gray-500">+ £{selectedPlan.setup_fee.toFixed(2)} setup fee</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Properties Summary */}
      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="font-semibold text-midnight-blue">
            Properties ({formData.properties.length} of {maxProps})
          </h3>
          <button 
            onClick={() => goToStep(3)} 
            className="text-sm text-electric-teal hover:underline"
            data-testid="edit-properties"
          >
            Edit
          </button>
        </div>
        <CardContent className="pt-4 divide-y">
          {formData.properties.map((prop, index) => (
            <div key={index} className={`py-3 ${index > 0 ? 'pt-3' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-midnight-blue">{getPropertyDisplayName(prop)}</p>
                  <p className="text-sm text-gray-600">{prop.address_line_1}, {prop.city}, {prop.postcode}</p>
                </div>
                <div className="flex gap-2">
                  {prop.jurisdiction && (
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{prop.jurisdiction}</span>
                  )}
                  {prop.is_hmo && (
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">HMO</span>
                  )}
                  {prop.licence_required === 'YES' && (
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Licensed</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b">
          <h3 className="font-semibold text-midnight-blue">Compliance profile summary</h3>
          <p className="text-xs text-gray-500 mt-1">
            Compliance requirements are generated automatically based on your property details and jurisdiction.
          </p>
        </div>
        <CardContent className="pt-4 space-y-3">
          {requirementsPreviewLoading && (
            <p className="text-sm text-gray-500">Generating summary...</p>
          )}
          {!requirementsPreviewLoading && requirementsPreviewError && (
            <p className="text-sm text-slate-700 leading-relaxed">{requirementsPreviewError}</p>
          )}
          {!requirementsPreviewLoading && !requirementsPreviewError && requirementsPreview.length === 0 && (
            <p className="text-sm text-gray-500">No preview available yet.</p>
          )}
          {!requirementsPreviewLoading && requirementsPreview.map((row, idx) => (
            <div key={idx} className="border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium text-midnight-blue">
                  {row.property_display_name || row.property_nickname || getPropertyDisplayName(formData.properties[idx] || {})}
                </p>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  {row.jurisdiction || 'Unknown jurisdiction'}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2">Based on the information provided, the platform will likely track obligations such as:</p>
              <ul className="mt-1 space-y-1 text-xs text-gray-700 list-disc pl-5">
                {(row.likely_obligations || row.top_generated_requirements || []).slice(0, 5).map((req, i) => (
                  <li key={`${idx}-${i}`}>{req}</li>
                ))}
                {(!row.likely_obligations || row.likely_obligations.length === 0) && <li>Core compliance certificate requirements</li>}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Additional requirements may appear after onboarding review or document verification.
              </p>
              {row.assumptions?.has_gas_supply_unknown_assumed_true_for_planning && (
                <p className="text-xs text-slate-600 mt-2 leading-relaxed border-l-2 border-slate-300 pl-3">
                  Gas supply is marked "Not sure"; planner currently assumes gas-supply obligations until confirmed.
                </p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Preferences Summary */}
      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="font-semibold text-midnight-blue">Preferences</h3>
          <button 
            onClick={() => goToStep(4)} 
            className="text-sm text-electric-teal hover:underline"
            data-testid="edit-preferences"
          >
            Edit
          </button>
        </div>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2">
            {formData.document_submission_method === 'UPLOAD' ? (
              <>
                <Upload className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Documents will be uploaded through the portal</span>
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">
                  You selected assisted document upload via email. You can send compliance documents to
                  {' '}
                  {branding.supportEmail}
                  . You can also upload documents directly after portal activation.
                </span>
              </>
            )}
          </div>
          {formData.document_submission_method === 'UPLOAD' && stagedFiles.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Staged uploads:</span>{' '}
              {stagedFiles.map((f) => f.original_filename || f.file_name || f.upload_id).join(', ')}
            </div>
          )}
          {formData.document_submission_method === 'UPLOAD' && stagedFiles.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">No files uploaded yet. You can add documents after account setup in your dashboard.</p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-gray-700">GDPR and service terms accepted</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b">
          <h3 className="font-semibold text-midnight-blue">Service agreement</h3>
          <p className="text-xs text-gray-500 mt-1">You must accept this agreement before payment. Text is loaded from our servers.</p>
        </div>
        <CardContent className="pt-4 sm:pt-6 space-y-5">
          {agreementLoading && <p className="text-sm text-slate-600">Loading agreement…</p>}
          {agreementError && !agreementLoading && (
            <div className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 leading-relaxed space-y-2" data-testid="intake-agreement-load-error">
              <p>{agreementError}</p>
              {agreementDiagnostic?.request_id && (
                <p className="text-xs text-slate-600">Reference: {agreementDiagnostic.request_id}</p>
              )}
              {!agreementDiagnostic?.request_id && agreementDiagnostic?.error_code && (
                <p className="text-xs text-slate-600">Code: {agreementDiagnostic.error_code}</p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetryAgreementPreview}
                data-testid="intake-agreement-retry"
              >
                Retry agreement preview
              </Button>
            </div>
          )}
          {!agreementLoading && !agreementCurrent && !agreementError && (
            <p className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 leading-relaxed" data-testid="intake-agreement-empty">
              No agreement content was returned. Refresh the page or try again later. If this persists, contact support.
            </p>
          )}
          {!agreementLoading && agreementCurrent && (
            <>
              {!agreementRenderValidation?.valid && (
                <p className="text-sm text-red-700" data-testid="intake-agreement-render-invalid">
                  The agreement content is not valid for payment yet. Refresh the page and try again.
                </p>
              )}
              <div className="border border-slate-200 rounded-lg p-4 sm:p-5 bg-white text-sm text-slate-800 space-y-4 leading-relaxed">
                <div>
                  <p className="font-semibold text-midnight-blue">{renderedAgreement?.title || agreementCurrent.title}</p>
                  {(renderedAgreement?.subtitle || agreementCurrent.subtitle) ? (
                    <p className="text-xs text-gray-500 mt-1">{renderedAgreement?.subtitle || agreementCurrent.subtitle}</p>
                  ) : null}
                  <p className="text-xs text-gray-500 mt-2">
                    Version {agreementCurrent?.version_number || '1'}
                    {agreementCurrent?.effective_from ? ` · Effective ${new Date(agreementCurrent.effective_from).toLocaleDateString()}` : ''}
                    {agreementCurrent?.published_at ? ` · Published ${new Date(agreementCurrent.published_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
                {((renderedAgreement?.document_structure?.sections || []).slice(0, 2)).map((section) => (
                  <div key={section.key || section.heading} className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{section.heading}</p>
                    {renderAgreementNodes(section)}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAgreementViewer(true)}
                  data-testid="view-full-agreement"
                >
                  View full agreement
                </Button>
              </div>
              <label className="flex items-start gap-3 sm:gap-4 cursor-pointer rounded-lg border border-slate-100 bg-slate-50/40 px-3 py-3 sm:p-4">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-electric-teal focus:ring-electric-teal"
                  checked={serviceAgreementAccepted}
                  disabled={!agreementRenderValidation?.valid}
                  onChange={(e) => onServiceAgreementAcceptedChange(e.target.checked)}
                  data-testid="intake-service-agreement-checkbox"
                />
                <span className="text-sm text-slate-800 leading-relaxed">{agreementCurrent.acceptance_text_required}</span>
              </label>
              <p className="text-xs text-slate-500 pl-3 sm:pl-4 max-w-prose leading-relaxed">
                Ticking this box alone does not store your acceptance — that happens only after you continue to payment
                and the server confirms.
              </p>
              {showAgreementViewer && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white w-full max-w-4xl h-[85vh] rounded-lg shadow-xl flex flex-col">
                    <div className="px-6 py-4 border-b">
                      <p className="font-semibold text-midnight-blue">{renderedAgreement?.title || agreementCurrent.title}</p>
                      {(renderedAgreement?.subtitle || agreementCurrent.subtitle) ? (
                        <p className="text-xs text-gray-500 mt-1">{renderedAgreement?.subtitle || agreementCurrent.subtitle}</p>
                      ) : null}
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                      {(renderedAgreement?.document_structure?.sections || []).map((section) => (
                        <section key={section.key || section.heading} className="space-y-2">
                          <h4 className="text-sm font-semibold uppercase tracking-wide text-midnight-blue">{section.heading}</h4>
                          {renderAgreementNodes(section)}
                        </section>
                      ))}
                    </div>
                    <div className="border-t px-6 py-4 bg-white sticky bottom-0 space-y-2">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-electric-teal focus:ring-electric-teal"
                          checked={serviceAgreementAccepted}
                          disabled={!agreementRenderValidation?.valid}
                          onChange={(e) => onServiceAgreementAcceptedChange(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700 flex-1">{agreementCurrent.acceptance_text_required}</span>
                        <Button type="button" variant="outline" onClick={() => setShowAgreementViewer(false)}>
                          Close
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 pl-7">
                        Ticking this box alone does not store your acceptance — that happens only after you continue to
                        payment and the server confirms.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Promo / pilot code (optional) */}
      <Card>
        <div className="px-6 py-3 bg-gray-50 border-b flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-midnight-blue">Have a code?</h3>
            <p className="text-xs text-gray-500 mt-1">
              Optional. Enter an authorised pilot or promo code — offers apply at secure checkout after validation.
            </p>
          </div>
          {(pilotInviteCode || '').trim() ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-gray-600 shrink-0"
              onClick={() => {
                onPilotInviteCodeChange('');
                onValidatePilotInvite('');
              }}
              data-testid="pilot-invite-clear"
            >
              Remove code
            </Button>
          ) : null}
        </div>
        <CardContent className="pt-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="pilot-invite-code">
              Code
            </label>
            <Input
              id="pilot-invite-code"
              value={pilotInviteCode || ''}
              onChange={(e) => {
                onPilotInviteCodeChange(e.target.value);
                if (!e.target.value.trim()) {
                  onValidatePilotInvite('');
                }
              }}
              onBlur={() => onValidatePilotInvite(pilotInviteCode)}
              placeholder="Enter code if you have one"
              data-testid="pilot-invite-code-input"
              autoComplete="off"
            />
          </div>
          {pilotInviteValidating && (
            <p className="text-xs text-gray-600 flex items-center gap-1.5" data-testid="pilot-invite-validating">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
              Checking invite code…
            </p>
          )}
          {!pilotInviteValidating && pilotInviteValidation?.valid && (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-3 space-y-1.5"
              data-testid="pilot-invite-valid"
            >
              <p className="text-sm font-semibold text-emerald-900 flex items-start gap-1.5">
                <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span>{pilotInviteValidation.headline || 'Founding Pilot Access Applied'}</span>
              </p>
              <p className="text-xs text-emerald-800 leading-relaxed pl-5">
                {pilotInviteValidation.detail
                  || pilotInviteValidation.message
                  || (pilotInviteValidation.onboarding_fee_waived
                    ? 'Your first 2 months are free, and your onboarding fee is waived. After the pilot, your selected subscription continues at the standard monthly rate unless cancelled before renewal.'
                    : 'Your founding pilot access will be processed through secure checkout.')}
              </p>
            </div>
          )}
          {!pilotInviteValidating && pilotInviteValidation && !pilotInviteValidation.valid && (
            <p className="text-xs text-red-700 flex items-start gap-1.5" data-testid="pilot-invite-invalid">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
              <span>{pilotInviteValidation.message}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Payment Summary */}
      {selectedPlan && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden border-t-4 border-t-electric-teal">
          <CardContent className="pt-6 sm:pt-8 pb-6">
            <h3 className="font-semibold text-midnight-blue mb-4">Payment Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly subscription</span>
                <span className="font-medium">£{selectedPlan.monthly_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">One-time setup fee</span>
                <span className="font-medium">
                  {pilotPricingActive && pilotInviteValidation.onboarding_fee_waived
                    ? 'Waived'
                    : `£${effectiveSetupFee.toFixed(2)}`}
                </span>
              </div>
              {pilotPricingActive && pilotInviteValidation.commercial_summary && (
                <p className="text-xs text-emerald-800 leading-relaxed border-t border-slate-100 pt-2">
                  {pilotInviteValidation.commercial_summary}
                </p>
              )}
              <div className="flex justify-between pt-2 border-t text-base">
                <span className="font-semibold text-midnight-blue">Total due today</span>
                <span className="font-bold text-electric-teal">
                  £{totalDueToday.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
        <Button variant="outline" onClick={onBack} className="flex-1 min-h-11" data-testid="step5-back">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={onSubmit} 
          disabled={loading || agreementBlocksPayment}
          className="flex-1 min-h-11"
          data-testid="submit-payment"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Proceed to Payment
            </>
          )}
        </Button>
      </div>

      {/* Cancel Link */}
      <div className="text-center">
        <button 
          onClick={() => window.location.href = '/'}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel and return to homepage
        </button>
      </div>
    </div>
  );
};

// Intake Document Upload Component (all file types; no count limit; size limits only)
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_SESSION_BYTES = 200 * 1024 * 1024;

const IntakeDocumentUpload = ({ intakeSessionId, onFilesChange }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadExistingFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/uploads/list/${intakeSessionId}`);
      if (res.ok) {
        const data = await res.json();
        setFiles(data);
        onFilesChange(data);
      }
    } catch (err) {
      console.error('Failed to load files:', err);
    }
  }, [API_URL, intakeSessionId, onFilesChange]);

  useEffect(() => {
    loadExistingFiles();
  }, [loadExistingFiles]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    await uploadFiles(droppedFiles);
  };

  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    await uploadFiles(selected);
    e.target.value = '';
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    for (const file of fileList) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} exceeds 20MB limit.`);
        return;
      }
    }
    const currentTotal = files.reduce((sum, f) => sum + (f.file_size || 0), 0);
    const newTotal = fileList.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + newTotal > MAX_SESSION_BYTES) {
      toast.error('Total would exceed 200MB for this session.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('intake_session_id', intakeSessionId);
      fileList.forEach(file => formData.append('files', file));
      const res = await fetch(`${API_URL}/api/intake/uploads/upload`, { method: 'POST', body: formData });
      const data = res.ok ? null : await res.json().catch(() => ({}));
      const detail = data?.detail;
      const message = typeof detail === 'object' ? detail?.message : detail;
      if (res.ok) {
        toast.success(`${fileList.length} file(s) uploaded`);
        await loadExistingFiles();
      } else {
        toast.error(message || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (uploadId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/intake/uploads/${uploadId}?intake_session_id=${encodeURIComponent(intakeSessionId)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('File removed');
        await loadExistingFiles();
      } else {
        const data = await res.json().catch(() => ({}));
        const msg = typeof data?.detail === 'object' ? data?.detail?.message : data?.detail;
        toast.error(msg || 'Failed to remove file');
      }
    } catch (err) {
      toast.error('Failed to remove file');
    }
  };

  const canDelete = (f) => f.status !== 'MIGRATED' && !f.migrated_to_document_id;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const totalSize = files.reduce((sum, f) => sum + f.file_size, 0);

  return (
    <div className="space-y-4">
      {/* Drag & Drop Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          dragActive ? 'border-electric-teal bg-electric-teal/5' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-midnight-blue mb-2">
          Drop files here or click to browse
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          All file types. Max 20MB per file, 200MB total per session.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-electric-teal hover:bg-electric-teal/90"
        >
          {uploading ? 'Uploading...' : 'Select Files'}
        </Button>
      </div>

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-midnight-blue">Uploaded Files ({files.length})</h4>
            <span className="text-sm text-gray-600">{formatFileSize(totalSize)} / 200 MB</span>
          </div>
          <div className="space-y-2">
            {files.map(file => (
              <div key={file.upload_id} className="flex items-center justify-between p-3 bg-white rounded border">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-electric-teal flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.original_filename}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.file_size)}
                      {file.status === 'CLEAN' && <span className="text-green-600 ml-2">• Ready</span>}
                      {file.status === 'QUARANTINED' && <span className="text-brand-warning ml-2 font-medium">• Not accepted</span>}
                      {file.status === 'MIGRATED' && <span className="text-gray-500 ml-2">• Migrated</span>}
                    </p>
                  </div>
                </div>
                {canDelete(file) && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeFile(file.upload_id)} className="text-red-600 hover:text-red-700 flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 italic">
        Documents uploaded here are optional. You can also add documents after account setup.
      </p>
    </div>
  );
};

export default IntakePage;
